import * as React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { setTags, setUser } from "@sentry/react";

import { UserBuddyStatus } from "pages/employee/types";

import { initFeatureConfig, isFeatureEnabled } from "feature-configs/featureConfigUtils";
import { FeatureConfig } from "feature-configs/featureConfigApi";

import * as authApi from "../auth/helpers/authApi";
import { User, UserPreferredLocale } from "../auth/types";
import { useLocalizationContext } from "localization/LocalizationProvider";

interface GameAuthContextValue {
	user?: User & { token: string; roomId: string; roomTemplateId: string };
	login: (email: string, password: string) => Promise<void>;
	passwordlessLogin: (roomTemplateId: string, token: string) => Promise<void>;
	loginToRoomFromUserDashboard: (roomId: string) => Promise<void>;
	logout: () => Promise<void>;
}

const GameAuthContext = React.createContext<GameAuthContextValue | undefined>(undefined);

export const GameAuthProvider: React.FC = (props) => {
	const navigate = useNavigate();
	const params = useParams();
	const location = useLocation();
	const { updateTranslations } = useLocalizationContext();

	const [authUser, setAuthUser] = React.useState<
		(User & { token: string; organizationSlug: string; roomTemplateId: string; roomId: string }) | undefined
	>(undefined);
	const [firstAttemptFinished, setFirstAttemptFinished] = React.useState<boolean>(false);

	React.useEffect(() => {
		if (firstAttemptFinished) {
			return;
		}

		const user = authApi.getUser();
		const token = authApi.getToken();
		const roomId = authApi.getRoomId();
		const roomTemplateId = authApi.getRoomTemplateId();
		const organizationSlug = authApi.getOrganizationSlug();
		const gameSlug = authApi.getGameSlug();
		const userDashboardSlug = authApi.getUserDashboardSlug();
		const isBuddyPage = location.pathname.includes("buddy");
		const isDashboardPage = location.pathname.includes("user-dashboard");
		const protectedPage = isBuddyPage || isDashboardPage;

		if (user) {
			setAuthUser({
				...user,
				token,
				roomId,
				roomTemplateId,
				organizationSlug,
			});
			setUser(user);
			setTags({
				roomId,
				roomTemplateId,
				organizationSlug,
			});
			updateTranslations({ locale: user.preferredLocale || UserPreferredLocale.NL });

			if (gameSlug) {
				// If user is already in a room, navigate to the room
				navigate(gameSlug, { replace: true });
			} else if (!user.isTestMode && !user.profilePicture) {
				navigate("onboarding/profile-picture", { replace: true });
			} else if (!user.isTestMode && (!user.gender || !user.birthDate || !user.phoneNumber)) {
				navigate("onboarding/profile", { replace: true });
			} else if (
				!user.isTestMode &&
				isFeatureEnabled(FeatureConfig.buddy) &&
				user.buddyStatus === UserBuddyStatus.INVITED
			) {
				navigate("buddy/invitation", { replace: true });
			} else if (
				!user.isTestMode &&
				isFeatureEnabled(FeatureConfig.buddy) &&
				user.buddyStatus === UserBuddyStatus.INCOMPLETE_QUESTIONAIRE
			) {
				navigate("buddy/user-questionnaire", { replace: true });
			} else if (params.slug === userDashboardSlug) {
				navigate("user-dashboard", { replace: true });
			} else if (gameSlug) {
				navigate(gameSlug, { replace: true });
			} else if (!gameSlug && !params.slug && !protectedPage) {
				navigate("user-dashboard", { replace: true });
			}
			initFeatureConfig().then(() => {
				setFirstAttemptFinished(true);
			});
		} else {
			setFirstAttemptFinished(true);
		}
		setFirstAttemptFinished(true);
	}, [
		firstAttemptFinished,
		location.pathname,
		navigate,
		params.slug,
		setAuthUser,
		setFirstAttemptFinished,
		updateTranslations,
	]);

	const login = React.useCallback(
		async (email: string, password: string) => {
			const response = await authApi.login({ email: email.toLowerCase(), password });
			await initFeatureConfig();
			setAuthUser({
				...response.user,
				token: response.token,
				roomId: response.room?.roomId || "",
				roomTemplateId: response.room?.roomTemplateId || "",
				organizationSlug: response.organization?.slug,
			});
			updateTranslations({ locale: response.user.preferredLocale || UserPreferredLocale.NL });

			const user = response.user;

			if (response?.room?.roomId && response?.gameSlug) {
				// If user is already in a room, navigate to the room
				return navigate(response.gameSlug);
			}
			if (!user.isTestMode && !user.profilePicture) {
				return navigate("onboarding/profile-picture", { replace: true });
			}
			if (!user.isTestMode && (!user.gender || !user.birthDate || !user.phoneNumber)) {
				return navigate("onboarding/profile", { replace: true });
			}
			if (
				!user.isTestMode &&
				isFeatureEnabled(FeatureConfig.buddy) &&
				user.buddyStatus === UserBuddyStatus.INVITED
			) {
				return navigate("buddy/invitation", { replace: true });
			}
			if (
				!user.isTestMode &&
				isFeatureEnabled(FeatureConfig.buddy) &&
				response.user?.buddyStatus === UserBuddyStatus.INCOMPLETE_QUESTIONAIRE
			) {
				return navigate("buddy/user-questionnaire");
			}
			navigate(response.gameSlug || "user-dashboard");
		},
		[navigate, updateTranslations],
	);

	const passwordlessLogin = React.useCallback(
		async (roomTemplateId: string, token: string) => {
			const response = await authApi.passwordlessLogin({ roomTemplateId, token });
			setAuthUser({
				...response.user,
				token: response.token,
				roomId: response.room?.roomId || "",
				roomTemplateId: response.room?.roomTemplateId || "",
				organizationSlug: response.organization?.slug,
			});
			await initFeatureConfig();
			navigate(response.gameSlug || "user-dashboard");
		},
		[navigate],
	);

	const loginToRoomFromUserDashboard = React.useCallback(
		async (roomId: string) => {
			const response = await authApi.fetchRoom({ roomId });
			authApi.setGameSlug(response.gameSlug);
			//@ts-ignore
			setAuthUser((prev) => ({
				...prev,
				roomId: response.room.roomId,
				roomTemplateId: response.room.roomTemplateId,
				organizationSlug: response.organization?.slug,
			}));
			navigate(response.gameSlug);
		},
		[navigate],
	);

	const logout = React.useCallback(async () => {
		const localStorageUserDashboardKey = "__auth-userDashboard";
		const localStorageRoomIdKey = "__auth-roomId";
		if (
			window.localStorage.getItem(localStorageUserDashboardKey) &&
			window.localStorage.getItem(localStorageRoomIdKey)
		) {
			authApi.userLogouttoUserDashboard();
			navigate("user-dashboard", { replace: true });
		} else {
			authApi.userLogout();
			setAuthUser(undefined);
			if (authUser?.isTestMode) {
				window.close();
				return;
			}
		}
	}, [authUser?.isTestMode, navigate]);

	const value = React.useMemo(
		() => ({
			login,
			logout,
			loginToRoomFromUserDashboard,
			user: authUser,
			passwordlessLogin,
		}),
		[login, logout, loginToRoomFromUserDashboard, authUser, passwordlessLogin],
	);

	window.addEventListener("focus", authApi.verifyToken as any);

	if (!firstAttemptFinished) {
		return <CircularProgress />;
	}

	return <GameAuthContext.Provider value={value}>{props.children}</GameAuthContext.Provider>;
};

export function useAuthContext(): GameAuthContextValue {
	const context = React.useContext(GameAuthContext);
	if (!context) {
		throw new Error("useAuth must be put within AuthProvider");
	}
	return context;
}

export function useAuthUser() {
	const { user } = useAuthContext();
	if (!user) {
		throw new Error("User should be logged in to use this hook");
	}

	return user;
}
